import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Grid, Paper, Alert, Autocomplete, Chip, Card, CardContent, IconButton,
  Collapse, Table, TableBody, TableCell, TableHead, TableRow, Button,
} from '@mui/material';
import { Add, Edit, Delete, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { useDataProvider } from 'react-admin';
import { buildTransitionChain } from '@/components/workflow/machineHelpers';
import { useRuleEvaluation } from '@/components/workflow/useRuleEvaluation';

const STEPS = [
  { id: 'personal', title: 'Personal Details' },
  { id: 'qualifications', title: 'Qualifications' },
  { id: 'availability', title: 'Availability' },
  { id: 'review', title: 'Review & Submit' },
];

interface EmployeeSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeNumber: string;
  department: string;
  jobTitle: string;
}

interface TrainingProviderResult {
  id: string;
  providerName: string;
  accreditationStatus: string;
}

interface QualificationTypeResult {
  _id: string;
  name: string;
  category: string;
}

interface QualificationEntry {
  trainingProviderId?: string;
  trainingProviderName?: string;
  issuer: string;
  qualificationTypeId?: string;
  qualificationTypeName?: string;
  name: string;
  issuedDate: string;
  expiryDate?: string;
  verified: boolean;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  idNumber: string;
  employeeId?: string;
  qualifications: QualificationEntry[];
  availableDays: string[];
  availableFrom: string;
  availableTo: string;
  notes: string;
}

const INITIAL_VALUES: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  idNumber: '',
  employeeId: undefined,
  qualifications: [],
  availableDays: [],
  availableFrom: '',
  availableTo: '',
  notes: '',
};

const stepDescriptions: Record<string, string> = {
  personal: "Enter the tutor's personal information. If they are an existing employee, search to auto-fill from employee records.",
  qualifications: "Add formal qualifications held by this tutor. Link to registered training providers when available.",
  availability: "Set the tutor's weekly availability. This helps when scheduling sessions.",
  review: "Review all information before creating the tutor record. You can go back to make changes.",
};

const EMPTY_QUALIFICATION: QualificationEntry = {
  issuer: '',
  name: '',
  issuedDate: '',
  verified: false,
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const TutorWizard = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const evaluateRef = useRef<(() => Promise<{ allowed: boolean; reason?: string }>) | null>(null);

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    if (evaluateRef.current) {
      const ruleResult = await evaluateRef.current();
      if (!ruleResult.allowed) {
        throw new Error(ruleResult.reason || 'Rule evaluation failed');
      }
    }

    const entityResult = await dataProvider.create('tutoring-tutors', {
      data: {
        firstName: v.firstName,
        lastName: v.lastName,
        email: v.email,
        phone: v.phone,
        idNumber: v.idNumber,
        ...(v.employeeId ? { employeeId: v.employeeId } : {}),
        availableDays: v.availableDays,
        availableFrom: v.availableFrom,
        availableTo: v.availableTo,
        notes: v.notes,
        status: 'Draft',
      },
    });

    const entityId = entityResult.data.id;

    if (v.qualifications.length > 0) {
      await dataProvider.create('qualifications/bulk', {
        data: {
          qualifications: v.qualifications.map(q => ({
            tutor: { type: 'tutor', id: entityId },
            qualificationType: q.qualificationTypeId
              ? { type: 'qualificationtype', id: q.qualificationTypeId }
              : { type: 'qualificationtype', id: '' },
            ...(q.trainingProviderId
              ? { trainingProvider: { type: 'trainingprovider', id: q.trainingProviderId } }
              : {}),
            name: q.name,
            issuer: q.issuer,
            issuedDate: q.issuedDate,
            expiryDate: q.expiryDate || undefined,
            verified: q.verified,
          })),
        },
      });
    }

    const { finalState, transitions } = buildTransitionChain('tutor', 'draft', ['SUBMIT']);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'tutor',
        entityId,
        entityName: `${v.firstName} ${v.lastName}`,
        currentState: { value: finalState, context: v },
        status: 'active',
        transitions,
      },
    });

    return { id: entityId };
  }, [dataProvider]);

  const {
    currentStep, currentStepIndex, totalSteps, isFirstStep, isLastStep,
    isSaving, isCompleted, progress, values, errors, stepFeedback,
    nextStep, prevStep, updateValues, complete, reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    onComplete: handleWizardComplete,
    stepValidators: {
      personal: (vals) => {
        const v = vals as unknown as FormValues;
        const errs: Record<string, string> = {};
        if (!v.firstName?.trim()) errs.firstName = 'First name is required';
        return { isValid: Object.keys(errs).length === 0, errors: errs };
      },
    },
  });

  const handleNext = () => {
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value });
  };

  const vals = values as unknown as FormValues;
  const { evaluate } = useRuleEvaluation({ ruleId: 'tutor:create', context: vals as unknown as Record<string, unknown> });
  useEffect(() => {
    evaluateRef.current = evaluate;
  }, [evaluate]);

  const [employeeSearchResults, setEmployeeSearchResults] = useState<EmployeeSearchResult[]>([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchEmployees = useCallback((query: string) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (!query.trim()) {
      setEmployeeSearchResults([]);
      return;
    }
    debounceTimerRef.current = setTimeout(async () => {
      setEmployeeSearchLoading(true);
      try {
        const result = await dataProvider.getList('employees', {
          pagination: { page: 1, perPage: 20 },
          sort: { field: 'employeeNumber', order: 'ASC' },
          filter: { search: query },
        });
        setEmployeeSearchResults(result.data.map((e: Record<string, unknown>) => ({
          id: e._id as string,
          firstName: (e as { firstName?: string }).firstName || '',
          lastName: (e as { lastName?: string }).lastName || '',
          email: (e as { email?: string }).email || '',
          employeeNumber: (e as { employeeNumber?: string }).employeeNumber || '',
          department: (e as { department?: string }).department || '',
          jobTitle: (e as { jobTitle?: string }).jobTitle || '',
        })));
      } catch {
        setEmployeeSearchResults([]);
      } finally {
        setEmployeeSearchLoading(false);
      }
    }, 300);
  }, [dataProvider]);

  const selectedEmployee = vals.employeeId
    ? employeeSearchResults.find(e => e.id === vals.employeeId) ?? null
    : null;

  const handleEmployeeSelect = useCallback((employee: EmployeeSearchResult | null) => {
    if (employee) {
      updateValues({
        employeeId: employee.id,
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
      });
    }
  }, [updateValues]);

  const handleClearEmployee = useCallback(() => {
    updateValues({ employeeId: undefined });
  }, [updateValues]);

  const [providerSearchResults, setProviderSearchResults] = useState<TrainingProviderResult[]>([]);
  const [providerSearchLoading, setProviderSearchLoading] = useState(false);
  const [editingQualIndex, setEditingQualIndex] = useState<number | null>(null);
  const [expandedQualIndex, setExpandedQualIndex] = useState<number | null>(null);
  const [newQualification, setNewQualification] = useState<QualificationEntry>({ ...EMPTY_QUALIFICATION });
  const providerDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchProviders = useCallback((query: string) => {
    if (providerDebounceRef.current) clearTimeout(providerDebounceRef.current);
    if (!query.trim()) {
      setProviderSearchResults([]);
      return;
    }
    providerDebounceRef.current = setTimeout(async () => {
      setProviderSearchLoading(true);
      try {
        const response = await fetch(`/api/v1/training-providers/search?q=${encodeURIComponent(query)}`);
        if (response.ok) {
          const data = await response.json();
          setProviderSearchResults(data.data || data);
        } else {
          setProviderSearchResults([]);
        }
      } catch {
        setProviderSearchResults([]);
      } finally {
        setProviderSearchLoading(false);
      }
    }, 300);
  }, []);

  const [qualTypeSearchResults, setQualTypeSearchResults] = useState<QualificationTypeResult[]>([]);
  const [qualTypeSearchLoading, setQualTypeSearchLoading] = useState(false);
  const qualTypeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchQualTypes = useCallback((query: string) => {
    if (qualTypeDebounceRef.current) clearTimeout(qualTypeDebounceRef.current);
    if (!query.trim()) {
      setQualTypeSearchResults([]);
      return;
    }
    qualTypeDebounceRef.current = setTimeout(async () => {
      setQualTypeSearchLoading(true);
      try {
        const result = await dataProvider.getList('qualification-types', {
          pagination: { page: 1, perPage: 20 },
          sort: { field: 'name', order: 'ASC' },
          filter: { search: query },
        });
        setQualTypeSearchResults(result.data.map((qt: Record<string, unknown>) => ({
          _id: qt._id as string,
          name: qt.name as string,
          category: qt.category as string,
        })));
      } catch {
        setQualTypeSearchResults([]);
      } finally {
        setQualTypeSearchLoading(false);
      }
    }, 300);
  }, [dataProvider]);

  const handleAddQualification = () => {
    const updated = [...vals.qualifications, { ...newQualification }];
    updateValues({ qualifications: updated });
    setNewQualification({ ...EMPTY_QUALIFICATION });
    setEditingQualIndex(null);
  };

  const handleUpdateQualification = () => {
    if (editingQualIndex === null) return;
    const updated = [...vals.qualifications];
    updated[editingQualIndex] = { ...newQualification };
    updateValues({ qualifications: updated });
    setNewQualification({ ...EMPTY_QUALIFICATION });
    setEditingQualIndex(null);
  };

  const handleRemoveQualification = (index: number) => {
    const updated = vals.qualifications.filter((_, i) => i !== index);
    updateValues({ qualifications: updated });
    if (expandedQualIndex === index) setExpandedQualIndex(null);
    if (editingQualIndex === index) {
      setEditingQualIndex(null);
      setNewQualification({ ...EMPTY_QUALIFICATION });
    }
  };

  const handleEditQualification = (index: number) => {
    setNewQualification({ ...vals.qualifications[index] });
    setEditingQualIndex(index);
  };

  const renderFormFields = () => {
    switch (currentStep.id) {
      case 'personal':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={employeeSearchResults}
                loading={employeeSearchLoading}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.employeeNumber})`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(_, value) => searchEmployees(value)}
                onChange={(_, value) => handleEmployeeSelect(value)}
                value={selectedEmployee}
                renderInput={(params) => (
                  <TextField {...params} label="Link to Employee (optional)" placeholder="Search by name or employee number" fullWidth />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.firstName} {option.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.employeeNumber} · {option.department}</Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            {vals.employeeId && (
              <Grid size={{ xs: 12 }}>
                <Card variant="outlined" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5 }}>
                  <CardContent sx={{ flex: 1, py: '8px !important' }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip label="Linked Employee" size="small" color="primary" />
                      {selectedEmployee && (
                        <Typography variant="body2" color="text.secondary">
                          {selectedEmployee.employeeNumber} · {selectedEmployee.department}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                  <Chip label="Clear" size="small" onDelete={handleClearEmployee} color="default" />
                </Card>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <input type="hidden" name="employeeId" value={vals.employeeId || ''} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="First Name" value={vals.firstName} onChange={e => updateField('firstName', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Last Name" value={vals.lastName} onChange={e => updateField('lastName', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Email" value={vals.email} onChange={e => updateField('email', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Phone" value={vals.phone} onChange={e => updateField('phone', e.target.value)} fullWidth />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="ID Number" value={vals.idNumber} onChange={e => updateField('idNumber', e.target.value)} fullWidth />
            </Grid>
          </Grid>
        );
      case 'qualifications':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Add qualifications for this tutor. Link to a training provider if registered in the system, otherwise enter the issuer name manually.
            </Alert>

            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.900', borderColor: 'grey.800', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {editingQualIndex !== null ? 'Edit Qualification' : 'Add Qualification'}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    options={providerSearchResults}
                    loading={providerSearchLoading}
                    getOptionLabel={(option) => option.providerName}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onInputChange={(_, value) => searchProviders(value)}
                    onChange={(_, value) => {
                      setNewQualification(prev => ({
                        ...prev,
                        trainingProviderId: value?.id,
                        trainingProviderName: value?.providerName,
                        issuer: value?.providerName || prev.issuer,
                      }));
                    }}
                    value={providerSearchResults.find(p => p.id === newQualification.trainingProviderId) || null}
                    renderInput={(params) => (
                      <TextField {...params} label="Training Provider (optional)" placeholder="Search providers..." fullWidth size="small" />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2">{option.providerName}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.accreditationStatus}</Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Issuer Name"
                    value={newQualification.issuer}
                    onChange={e => setNewQualification(prev => ({ ...prev, issuer: e.target.value }))}
                    fullWidth
                    size="small"
                    placeholder="Enter if not a registered provider"
                    disabled={!!newQualification.trainingProviderId}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    options={qualTypeSearchResults}
                    loading={qualTypeSearchLoading}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    onInputChange={(_, value) => searchQualTypes(value)}
                    onChange={(_, value) => {
                      setNewQualification(prev => ({
                        ...prev,
                        qualificationTypeId: value?._id,
                        qualificationTypeName: value?.name,
                      }));
                    }}
                    value={qualTypeSearchResults.find(qt => qt._id === newQualification.qualificationTypeId) || null}
                    renderInput={(params) => (
                      <TextField {...params} label="Qualification Type" placeholder="Search types..." fullWidth size="small" />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Qualification Name"
                    value={newQualification.name}
                    onChange={e => setNewQualification(prev => ({ ...prev, name: e.target.value }))}
                    fullWidth
                    size="small"
                    placeholder="e.g., BSc Computer Science"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Date Completed"
                    type="date"
                    value={newQualification.issuedDate}
                    onChange={e => setNewQualification(prev => ({ ...prev, issuedDate: e.target.value }))}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Expiry Date (optional)"
                    type="date"
                    value={newQualification.expiryDate || ''}
                    onChange={e => setNewQualification(prev => ({ ...prev, expiryDate: e.target.value || undefined }))}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Verified</InputLabel>
                    <Select
                      value={newQualification.verified ? 'yes' : 'no'}
                      label="Verified"
                      onChange={e => setNewQualification(prev => ({ ...prev, verified: e.target.value === 'yes' }))}
                    >
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    {editingQualIndex !== null && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setEditingQualIndex(null);
                          setNewQualification({ ...EMPTY_QUALIFICATION });
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={editingQualIndex !== null ? <Edit /> : <Add />}
                      onClick={editingQualIndex !== null ? handleUpdateQualification : handleAddQualification}
                      disabled={!newQualification.name.trim() || !newQualification.issuer.trim()}
                    >
                      {editingQualIndex !== null ? 'Update' : 'Add'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {vals.qualifications.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Qualification</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Provider / Issuer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vals.qualifications.map((q, idx) => (
                    <>
                      <TableRow key={`row-${idx}`} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{q.name}</Typography>
                          {q.qualificationTypeName && (
                            <Typography variant="caption" color="text.secondary">{q.qualificationTypeName}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {q.trainingProviderName ? (
                            <Chip label={q.trainingProviderName} size="small" color="primary" variant="outlined" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">{q.issuer || '—'}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{q.issuedDate}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => setExpandedQualIndex(expandedQualIndex === idx ? null : idx)}>
                            {expandedQualIndex === idx ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <IconButton size="small" onClick={() => handleEditQualification(idx)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleRemoveQualification(idx)} color="error">
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow key={`detail-${idx}`}>
                        <TableCell colSpan={4} sx={{ py: 0 }}>
                          <Collapse in={expandedQualIndex === idx} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1, px: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                {q.verified ? '✓ Verified' : 'Not verified'}
                                {q.expiryDate ? ` · Expires: ${q.expiryDate}` : ''}
                              </Typography>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        );
      case 'availability':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Available Days</InputLabel>
                <Select
                  multiple
                  value={vals.availableDays}
                  label="Available Days"
                  onChange={e => updateField('availableDays', e.target.value)}
                >
                  {DAYS_OF_WEEK.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Available From" type="time" value={vals.availableFrom} onChange={e => updateField('availableFrom', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Available To" type="time" value={vals.availableTo} onChange={e => updateField('availableTo', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Notes" value={vals.notes} onChange={e => updateField('notes', e.target.value)} fullWidth multiline rows={3} />
            </Grid>
          </Grid>
        );
      case 'review':
        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Review tutor details before creating. A workflow will be initialized in Draft state.</Alert>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Personal Info</Typography>
                <Typography variant="body2"><strong>Name:</strong> {vals.firstName} {vals.lastName}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {vals.email}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {vals.phone}</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Availability</Typography>
                <Typography variant="body2"><strong>Days:</strong> {vals.availableDays.join(', ')}</Typography>
                <Typography variant="body2"><strong>Time:</strong> {vals.availableFrom} - {vals.availableTo}</Typography>
              </Paper>
            </Grid>
            {vals.qualifications.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Qualifications ({vals.qualifications.length})</Typography>
                  {vals.qualifications.map((q, idx) => (
                    <Box key={idx} sx={{ mb: 1, pb: 1, borderBottom: idx < vals.qualifications.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                      <Typography variant="body2"><strong>{q.name}</strong></Typography>
                      <Typography variant="caption" color="text.secondary">
                        {q.trainingProviderName || q.issuer} · {q.issuedDate} · {q.verified ? 'Verified' : 'Not verified'}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            )}
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={prevStep}
      onSaveDraft={() => {}}
      onNext={handleNext}
      onComplete={complete}
      onReset={reset}
    >
      {isCompleted ? (
        <OutcomeSummary
          title="Tutor Created"
          processId={(values as { id: string }).id}
          fields={[
            { label: 'Name', value: `${vals.firstName} ${vals.lastName}` },
            { label: 'Email', value: vals.email },
            { label: 'Qualifications', value: `${vals.qualifications.length} qualification(s)` },
            { label: 'Availability', value: vals.availableDays.join(', ') },
          ]}
          footerActions={[
            { label: 'View Tutors', to: '/tutoring-tutors' },
            { label: 'Create Another', onClick: reset },
          ]}
        />
      ) : (
        <WizardStep
          stepNumber={currentStepIndex + 1}
          totalSteps={totalSteps}
          title={currentStep.title}
          description={stepDescriptions[currentStep.id]}
        >
          {stepFeedback && <StepFeedback type={stepFeedback.type} messages={stepFeedback.messages} />}
          {renderFormFields()}
        </WizardStep>
      )}
    </WizardLayout>
  );
};
